import { PeriodoPontoFactory, Void } from '@acme/common'
import { Box, BoxProps, Typography } from '@mui/material'
import BntGroup, { ButtonGroupProps } from '@mui/material/ButtonGroup'
import {
    DatePicker,
    DatePickerProps,
    LocalizationProvider,
    MonthCalendar,
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { createContext, useEffect, useState } from 'react'
import { useStore } from 'store'
import { match } from 'ts-pattern'
import { Button } from '../Button'
import Menu from '../MenuV2'

const FORMATO_COMPETENCIA = 'YYYY-MM' as const

const Context = createContext({
    state: dayjs().format(FORMATO_COMPETENCIA),
    size: 'small' as 'small' | 'medium' | undefined,
    label: 'Competência' as string | undefined,
    variant: 'filled' as 'filled' | 'outlined' | undefined,
    handlePrev: Void,
    handleNext: Void,
    handleChange: Void as IRootProps['onChange'],
})

interface IRootProps {
    size?: 'small' | 'medium'
    label?: string
    variant?: 'filled' | 'outlined'
    value: string
    onChange: React.Dispatch<React.SetStateAction<string>>
    readOnly?: boolean
    width?: number
    hideButtons?: boolean
}
type RootProps = { BoxProps?: Omit<BoxProps, keyof IRootProps> } & IRootProps

export default function SelectCompetencia({
    size = 'small',
    label,
    variant = 'filled',
    onChange,
    value,
    readOnly,
    width,
    hideButtons,
    BoxProps,
}: RootProps) {
    const setStore = useStore(state => state.setCompetence)

    useEffect(() => {
        if (value) setStore(value)
    }, [value])

    width = (() => {
        if (width != null) return width
        if (hideButtons) return 220
        return 320
    })()

    const handleNext = React.useCallback(() => {
        onChange(old => {
            return dayjs(old, FORMATO_COMPETENCIA)
                .add(1, 'month')
                .format(FORMATO_COMPETENCIA)
        })
    }, [value, onChange])

    const handlePrev = React.useCallback(() => {
        onChange(old => {
            return dayjs(old, FORMATO_COMPETENCIA)
                .subtract(1, 'month')
                .format(FORMATO_COMPETENCIA)
        })
    }, [value, onChange])

    return (
        <Context.Provider
            value={{
                state: value,
                handlePrev,
                handleNext,
                handleChange: onChange,
                size,
                label,
                variant,
            }}
        >
            <Box
                width={width ?? 'fit-content'}
                display="flex"
                gap={1}
                alignItems="center"
                {...BoxProps}
            >
                <DateSelector readOnly={readOnly} />
                {!hideButtons && <Buttons />}
            </Box>
        </Context.Provider>
    )
}

const sizePattern = (size?: 'small' | 'medium') =>
    match(size)
        .with('small', () => 40)
        .with('medium', () => 55)
        .otherwise(() => 40)

function DateSelector({
    sx,
    readOnly,
    ...props
}: DatePickerProps<dayjs.Dayjs>) {
    return (
        <Context.Consumer>
            {({
                state,
                handleChange,
                variant,
                label = 'Competência',
                size,
            }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {(() => {
                        const Picker = () => (
                            <Button
                                variant="text"
                                sx={{
                                    p: 0,
                                    height: sizePattern(size),
                                    ...(variant === 'filled' && {
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    }),
                                    ...sx,
                                }}
                                disabled={props.disabled || readOnly}
                            >
                                <DatePicker
                                    {...props}
                                    slotProps={{
                                        ...props.slotProps,
                                        textField: {
                                            variant,
                                            size,
                                            label,
                                            ...props.slotProps?.textField,
                                        },
                                    }}
                                    format={'MM/YYYY (MMMM)'}
                                    value={dayjs(state, FORMATO_COMPETENCIA)}
                                    readOnly
                                    sx={{
                                        pointerEvents: 'none',
                                        width: '100%',
                                        height: '100%',
                                        ...(variant === 'filled' && {
                                            mt: -1,
                                        }),
                                        '& .MuiFormLabel-root': {
                                            cursor: readOnly
                                                ? 'auto'
                                                : 'pointer',
                                            textTransform: 'capitalize',
                                        },
                                    }}
                                />
                            </Button>
                        )

                        if (readOnly) return Picker()
                        return (
                            <Menu.Root>
                                <Menu.Trigger>{Picker()}</Menu.Trigger>
                                <Menu.List
                                    onClick={Void}
                                    sx={{
                                        '& .MuiList-root': {
                                            py: 0,
                                            width: 'fit-content',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 'fit-content',
                                                    width: '100%',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    px: 1,
                                                    my: 1,
                                                    display: 'inline-flex',
                                                }}
                                            >
                                                <Button
                                                    onClick={() => {
                                                        const competencia =
                                                            dayjs(
                                                                state,
                                                                FORMATO_COMPETENCIA,
                                                            )
                                                                .subtract(
                                                                    1,
                                                                    'year',
                                                                )
                                                                .set(
                                                                    'month',
                                                                    11,
                                                                )
                                                                .format(
                                                                    FORMATO_COMPETENCIA,
                                                                )
                                                        handleChange(
                                                            competencia,
                                                        )
                                                    }}
                                                    sx={{
                                                        width: 45,
                                                        height: 45,
                                                    }}
                                                    title="Ano anterior"
                                                    variant="outlined"
                                                >
                                                    <div className="flex items-center">
                                                        <ChevronLeftIcon />
                                                    </div>
                                                </Button>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ userSelect: 'none' }}
                                                >
                                                    {dayjs(
                                                        state,
                                                        FORMATO_COMPETENCIA,
                                                    ).format('YYYY')}
                                                </Typography>
                                                <Button
                                                    onClick={() => {
                                                        const competencia =
                                                            dayjs(
                                                                state,
                                                                FORMATO_COMPETENCIA,
                                                            )
                                                                .add(1, 'year')
                                                                .set('month', 0)
                                                                .format(
                                                                    FORMATO_COMPETENCIA,
                                                                )
                                                        handleChange(
                                                            competencia,
                                                        )
                                                    }}
                                                    sx={{
                                                        width: 45,
                                                        height: 45,
                                                    }}
                                                    title="Próximo ano"
                                                    variant="outlined"
                                                >
                                                    <div className="flex items-center">
                                                        <ChevronRightIcon />
                                                    </div>
                                                </Button>
                                            </Box>
                                            <MonthCalendar
                                                onChange={(
                                                    competencia: Dayjs,
                                                ) => {
                                                    handleChange(
                                                        competencia.format(
                                                            FORMATO_COMPETENCIA,
                                                        ),
                                                    )
                                                }}
                                                value={dayjs(
                                                    state,
                                                    FORMATO_COMPETENCIA,
                                                )}
                                            />
                                            <DisplayPeriodo
                                                competencia={state}
                                            />
                                        </Box>
                                    </Box>
                                </Menu.List>
                            </Menu.Root>
                        )
                    })()}
                </LocalizationProvider>
            )}
        </Context.Consumer>
    )
}

interface IPropsDisplay {
    competencia: string
}

function DisplayPeriodo({ competencia }: IPropsDisplay) {
    const empresa = useStore(state => state.company)
    if (!empresa || !empresa.diaInicioPonto || !empresa.TrocaInicioPonto)
        return null

    const [dataInicial, dataFinal] = PeriodoPontoFactory.fromCompetencia(
        competencia,
        {
            diaInicioPonto: empresa.diaInicioPonto,
            trocasInicioPonto: empresa.TrocaInicioPonto,
        },
    ).getPeriodo()

    return (
        <Typography sx={{ textAlign: 'center', py: 2, userSelect: 'none' }}>
            Período de <strong>{dataInicial.format('DD/MM/YYYY')}</strong> a{' '}
            <strong>{dataFinal.format('DD/MM/YYYY')}</strong>
        </Typography>
    )
}

function Buttons(props: ButtonGroupProps) {
    return (
        <Context.Consumer>
            {({ handlePrev, handleNext, size, variant }) => (
                <BntGroup
                    {...props}
                    sx={{
                        height: sizePattern(size),
                        ...(variant === 'filled' && { mt: -1 }),
                        ...props.sx,
                    }}
                >
                    <Button
                        onClick={handlePrev}
                        sx={{
                            width: 48,
                            height:
                                variant === 'outlined'
                                    ? '100%'
                                    : 'calc(100% + 8px)',
                            border: 1,
                        }}
                        title="Data anterior"
                        variant={match(variant)
                            .with('filled', () => 'text' as const)
                            .otherwise(() => 'outlined' as const)}
                    >
                        <div className="flex items-center">
                            <ChevronLeftIcon />
                        </div>
                    </Button>
                    <Button
                        onClick={handleNext}
                        sx={{
                            width: 48,
                            height:
                                variant === 'outlined'
                                    ? '100%'
                                    : 'calc(100% + 8px)',
                            border: 1,
                        }}
                        title="Próxima data"
                        variant={match(variant)
                            .with('filled', () => 'text' as const)
                            .otherwise(() => 'outlined' as const)}
                    >
                        <div className="flex items-center">
                            <ChevronRightIcon />
                        </div>
                    </Button>
                </BntGroup>
            )}
        </Context.Consumer>
    )
}

type DefaultCompetencia =
    | string
    | ((value: string, formato: typeof FORMATO_COMPETENCIA) => string)

export function useCompetencia(defaultValue?: DefaultCompetencia) {
    const empresa = useStore(state => state.company)
    const competenciaAberta = empresa?.competenciaAtual

    const DEFAULT = (() => {
        if (typeof defaultValue === 'function')
            return defaultValue(competenciaAberta || '', FORMATO_COMPETENCIA)
        if (typeof defaultValue === 'string') return defaultValue
        return dayjs(competenciaAberta).format(FORMATO_COMPETENCIA)
    })()

    const [competencia, _set] = useState<string>(DEFAULT)

    function setCompetencia(
        value: string | ((prev: string) => string) | null | dayjs.Dayjs,
    ) {
        if (value === null) return _set(DEFAULT)

        if (dayjs.isDayjs(value)) {
            _set(value.format(FORMATO_COMPETENCIA))
        } else if (typeof value === 'function') {
            _set(prev => {
                const newValue = value(prev)
                return newValue
            })
        } else {
            _set(value)
        }
    }

    useEffect(() => {
        if (
            empresa &&
            empresa.competenciaAtual &&
            (typeof defaultValue === 'function'
                ? competencia !=
                  defaultValue(empresa.competenciaAtual, FORMATO_COMPETENCIA)
                : competencia != empresa.competenciaAtual)
        ) {
            setCompetencia(
                typeof defaultValue === 'function'
                    ? defaultValue(
                          empresa.competenciaAtual,
                          FORMATO_COMPETENCIA,
                      )
                    : dayjs(empresa.competenciaAtual).format(
                          FORMATO_COMPETENCIA,
                      ),
            )
        }
    }, [empresa?.id])

    return [competencia, setCompetencia] as const
}
